/**
 * Die Arbeitsplatz-Einstellungen: alles, was der Bestand in `localStorage` haelt und NICHT auf dem
 * Server liegt. Spec: „Client-lokale Einstellungen bleiben in `localStorage`".
 *
 * ================= Warum die Schluesselnamen unveraendert bleiben =================
 *
 * Ein Nutzer, der von `index.html` auf die SPA wechselt, behaelt seine Einstellungen nur dann, wenn
 * die Schluessel Zeichen fuer Zeichen dieselben sind. `SCHLUESSEL` ist deshalb eine gefrorene
 * Tabelle, und der Komponententest vergleicht sie hart gegen die vier Namen aus dem Bestand.
 *
 * ================= Die fuenf Schluessel und ihre Form im Bestand =================
 *
 * | Schluessel            | index.html | Form                       | Leser in diesem Baum          |
 * |-----------------------|------------|----------------------------|-------------------------------|
 * | `hubTheme`            | 5332       | Text `'light'` / `'dark'`  | KEINER (Kopfbereich, Task 7)  |
 * | `hubDrawerWidth`      | 4640, 9237 | Zahl ALS TEXT              | `Drawer.tsx`: Leser eigen, Schreiber Kapsel (Task 16) |
 * | `hubBoardSections`    | 4635, 6697 | JSON-Objekt                | `BoardSeite` (Task 13)        |
 * | `hubOrderListWidths`  | 4639, 7561 | JSON-Objekt                | `BoardSeite`, `Spaltenkopf`   |
 * | `hubItemTableWidths`  | 4638, 6873 | JSON-Objekt                | `MainTab/Positionen` (App)    |
 *
 * DER FUENFTE KAM AM 2026-08-15 DAZU, und sein Fehlen war genau der Schaden, vor dem der Absatz
 * darunter warnt: `Drawer/MainTab/Positionen.tsx` las `hubItemTableWidths` mit einer EIGENEN,
 * modulprivaten `localStorage`-Klammer - der „zweite, abweichende Zugang", den diese Kapsel
 * verhindern soll. Sichtbar wurde es erst beim Verdrahten von `onSpaltenbreiten`: das
 * Zuruecksetzen konnte den Wert gar nicht erreichen.
 *
 * Nur DREI der fuenf sind JSON. `hubDrawerWidth` liest der Bestand mit `readLocalValue` und schreibt
 * ihn mit `String(Math.round(...))` (9237); `hubTheme` ist eine nackte Zeichenkette (5349, 5356).
 * Wer das vereinheitlicht, macht die Einstellungen eines Bestandsnutzers unlesbar. GEMELDET:
 * `hubTheme` hat in diesem Baum noch keinen Leser - seine Flaeche ist nicht portiert. `hubDrawerWidth`
 * dagegen hat seit Task 16 (`Drawer.tsx`) sowohl Leser als auch Schreiber: gelesen wird dort ueber
 * einen EIGENEN Zugriff (Modul-Global, siehe die GEMELDETE ABWEICHUNG in `Drawer.tsx`), geschrieben
 * ueber diese Kapsel (`schreibeDrawerBreite`, unten) - `leseDrawerBreite` (unten) bleibt trotzdem
 * unbenutzt. `hubTheme` steht weiterhin hier, weil die Kapsel laut Task-Brief alle vier fuehrt und
 * weil ein spaeterer Task sonst einen zweiten, abweichenden Zugang bauen wuerde.
 *
 * ================= BEFUND: die Kapsel BEGRENZT NICHT =================
 *
 * `ORDER_LIST_WIDTHS = readLocalJson('hubOrderListWidths', {})` (index.html:4639) uebernimmt den
 * Rohwert. Die Begrenzung auf `min`/`max` sitzt woanders:
 *  - `orderListColumnWidth(key)` (7513) beim JEDEM Lesezugriff - `lib/orderList.ts:293`,
 *  - `setOrderListColumnWidth(key, width)` (7539) beim Schreiben einer neuen Breite -
 *    `SpaltenBreiten.ts`.
 *
 * Ein von Hand in `localStorage` gesetzter Wert von 99999 bleibt dort also stehen und wird bei jeder
 * Verwendung erneut gekappt; die Kapsel schreibt ihn NICHT um. Der Task-Brief verlangt die
 * Begrenzung an dieser Stelle - das waere eine Verhaltensaenderung (der gespeicherte Wert
 * ueberlebte einen Rundlauf nicht mehr) und ist deshalb nicht so umgesetzt. Siehe Bericht, B4.
 *
 * ================= Woertlich uebernommene Eigenheiten =================
 *
 * 1. `readLocalJson` (index.html:4679) verwirft alles, was kein Objekt ist - auch `[]`? NEIN: ein
 *    Array IST ein Objekt und kommt durch. Verworfen werden `null`, Zahlen, Zeichenketten und
 *    Wahrheitswerte, und zwar ueber `value && typeof value === 'object'`.
 * 2. Jeder Zugriff steht in `try/catch`. Der Bestand rechnet damit, dass `localStorage` wirft
 *    (Privatmodus, gesperrte Drittanbieter-Daten, voller Speicher) - ohne die Klammer riss ein
 *    kaputter Eintrag die ganze Seite mit. `JSON.parse('{kaputt')` wirft, das `catch` liefert den
 *    Rueckfall.
 * 3. `localStorage.getItem(key) || 'null'` - ein leerer String wird zu `'null'` und damit zum
 *    Rueckfall, statt `JSON.parse('')` werfen zu lassen.
 * 4. `Number(readLocalValue('hubDrawerWidth', '640')) || 640` (4640): `|| 640` faengt sowohl `NaN`
 *    als auch die **0**. Eine gespeicherte Breite von 0 ergibt 640, nicht 0.
 *
 * ================= Der SECHSTE Schluessel (NEUBAU ohne Bestandsvorbild) ========================
 *
 * `hubCountryFilter` (EU/China-Laenderfilter im Bestellboard, `Board/Werkzeugleiste.tsx`) kam am
 * 2026-08-26 dazu und unterliegt AUSDRUECKLICH NICHT dem Namensvertrag oben: `index.html` kennt
 * dieses Feature nicht, es gibt also keinen Bestandsschluessel, dessen Zeichenfolge zu wahren waere.
 * Die fuenf Namen darueber bleiben deshalb genau wie sie sind - eingefroren, unveraendert, Zeichen
 * fuer Zeichen der Bestandsvertrag. Der sechste ist reine Namensfreiheit dieses Baums, gewaehlt in
 * derselben `hub`-Namenskonvention, damit er sich unter den anderen fuenf nicht wie ein Fremdkoerper
 * liest.
 *
 * ================= Der SIEBTE Schluessel (NEUBAU ohne Bestandsvorbild) =========================
 *
 * `hubTransportModeFilter` (Luftfracht/Seefracht-Filter, Auftrag „Luftfracht/Seefracht-Filter im
 * Bestellhub", 2026-08-27) kam analog zum sechsten dazu: `index.html` kennt auch dieses Feature
 * nicht, derselbe Freibrief gilt also erneut. Namensschema wie beim sechsten (`hub` + Kurzbegriff),
 * Form und Fallback wortgleich zu `hubCountryFilter` gebaut - siehe `leseTransportartFilter` unten.
 *
 * ================= Der entfallene ACHTE Schluessel (`hubActivityEventFilter`) =================
 *
 * Bis zur Seite "Aktivitaeten" (Phase 1, Task 9) hielt ein achter Schluessel, `hubActivityEventFilter`,
 * die Event-Typ-Auswahl der Kachel "Letzte Aktivitaeten" der Uebersicht. Mit der Kachel sind der
 * Schluessel und seine Lese-/Schreibfunktionen entfallen; die Seite haelt ihre Filter in der Adresse
 * (`state/adresse.ts`), nicht im `localStorage`. Ein bei einem Nutzer noch vorhandener Eintrag bleibt
 * unbeachtet im Speicher liegen - er wird nirgends mehr gelesen und ist harmlos.
 */
import type { LaenderFilter } from "../lib/laender.ts";
import type { TransportartFilter } from "../lib/transportart.ts";

// Beide Filtertypen wohnen seit dem KI-Zugang (Stufe 1) in `lib/` (der Server rechnet Kosten und
// Uebersicht mit denselben Modulen und darf kein Zustandsmodul mit `localStorage` in seiner
// Importkette haben); hier bleiben die Namen erreichbar.
export type { LaenderFilter, TransportartFilter };

/**
 * Die sieben `localStorage`-Schluessel dieses Baums. Gefroren, damit kein Aufrufer einen Namen
 * ueberschreibt - die ERSTEN FUENF sind der Vertrag mit dem Bestand (siehe Kopfkommentar), SECHSTER
 * und SIEBTER sind eigene, siehe „Der sechste/siebte Schluessel" oben.
 */
export const SCHLUESSEL = Object.freeze({
  theme: "hubTheme",
  drawerBreite: "hubDrawerWidth",
  boardAbschnitte: "hubBoardSections",
  listenBreiten: "hubOrderListWidths",
  artikelBreiten: "hubItemTableWidths",
  laenderFilter: "hubCountryFilter",
  transportartFilter: "hubTransportModeFilter",
});

/** index.html:4640 - Vorgabebreite des Bestell-Drawers in Pixeln. */
export const DRAWER_BREITE_VORGABE = 640;

/** index.html:4676 */
function readLocalValue(key: string, fallback = ""): string {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch { // still: wie im Bestand (index.html:4676) darf ein blockiertes localStorage (privates Fenster o. ae.) die Anwendung nicht verhindern - es gilt dann die Vorgabe
    return fallback;
  }
}

/** index.html:4679 */
function readLocalJson<T extends object>(key: string, fallback: T): T {
  try {
    const value = JSON.parse(localStorage.getItem(key) || "null");
    return value && typeof value === "object" ? (value as T) : fallback;
  } catch { // still: wie im Bestand (index.html:4679) gilt bei blockiertem oder kaputtem localStorage-Eintrag die Vorgabe
    return fallback;
  }
}

/** index.html:4685 */
function writeLocalJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch { // still: leer wie im Bestand (index.html:4685) - ein nicht schreibbarer Speicher ist kein Fehler, nur folgenlos (die Einstellung gilt dann bis zum Neuladen)
  }
}

/** Roher Wert eines Schluessels schreiben - fuer die beiden NICHT-JSON-Schluessel. */
function writeLocalValue(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch { // still: siehe `writeLocalJson` - ein nicht schreibbarer Speicher ist folgenlos
  }
}

export type Breiten = Record<string, unknown>;
export type Abschnitte = Record<string, unknown>;

/**
 * `ORDER_LIST_WIDTHS` (index.html:4639). Liefert den ROHWERT - siehe BEFUND im Kopfkommentar.
 * Gekappt wird erst in `orderListColumnWidth` (`lib/orderList.ts`).
 */
export function leseBreiten(): Breiten {
  return readLocalJson<Breiten>(SCHLUESSEL.listenBreiten, {});
}

/** `writeLocalJson('hubOrderListWidths', ORDER_LIST_WIDTHS)` (index.html:7561/7573/7578). */
export function schreibeBreiten(breiten: Breiten): void {
  writeLocalJson(SCHLUESSEL.listenBreiten, breiten);
}

/**
 * `ITEM_TABLE_WIDTHS` (index.html:4638) - die Spaltenbreiten der Artikeltabelle im Drawer.
 * Rohwert wie bei `leseBreiten`; gekappt wird in `Drawer/MainTab/Positionen.tsx`.
 */
export function leseArtikelBreiten(): Breiten {
  return readLocalJson<Breiten>(SCHLUESSEL.artikelBreiten, {});
}

/** `writeLocalJson('hubItemTableWidths', ITEM_TABLE_WIDTHS)` (index.html:6873). */
export function schreibeArtikelBreiten(breiten: Breiten): void {
  writeLocalJson(SCHLUESSEL.artikelBreiten, breiten);
}

/** `BOARD_SECTION_COLLAPSED` (index.html:4635). */
export function leseBoardAbschnitte(): Abschnitte {
  return readLocalJson<Abschnitte>(SCHLUESSEL.boardAbschnitte, {});
}

/** `writeLocalJson('hubBoardSections', BOARD_SECTION_COLLAPSED)` (index.html:6697). */
export function schreibeBoardAbschnitte(abschnitte: Abschnitte): void {
  writeLocalJson(SCHLUESSEL.boardAbschnitte, abschnitte);
}

/**
 * `toggleBoardSection(id)` (index.html:6694-6698) in seiner reinen Form: der Bestand kippt den
 * Eintrag und schreibt danach die ganze Tabelle. Das Kippen liefert hier eine NEUE Tabelle, damit
 * React die Aenderung sieht; das Schreiben bleibt Sache des Aufrufers (`schreibeBoardAbschnitte`).
 */
export function kippeAbschnitt(abschnitte: Abschnitte, gruppenId: string): Abschnitte {
  return { ...abschnitte, [gruppenId]: !abschnitte[gruppenId] };
}

/**
 * `initTheme` (index.html:5354-5358). Der Bestand faellt auf `'light'` zurueck und normalisiert erst
 * in `applyTheme`: alles ausser `'dark'` wird `'light'` (5347).
 */
export function leseTheme(): "light" | "dark" {
  return readLocalValue(SCHLUESSEL.theme, "light") === "dark" ? "dark" : "light";
}

/** `applyTheme(theme, true)` (index.html:5346-5351) - geschrieben wird der NORMALISIERTE Wert. */
export function schreibeTheme(theme: string): void {
  writeLocalValue(SCHLUESSEL.theme, theme === "dark" ? "dark" : "light");
}

/** Vorgabe, solange nichts gespeichert ist: beide Regionen sichtbar. */
const LAENDERFILTER_VORGABE: LaenderFilter = { eu: true, china: true };

/**
 * `hubCountryFilter` - NEUBAU ohne Bestandsvorbild, siehe Kopfkommentar „Der sechste Schluessel".
 * JSON-Form wie `leseBoardAbschnitte`; Vorgabe beim Fehlen/bei kaputtem JSON: `{ eu: true, china:
 * true }` (beide Regionen sichtbar - ein neuer Nutzer sieht das Board ungefiltert).
 */
export function leseLaenderFilter(): LaenderFilter {
  const wert = readLocalJson<Partial<LaenderFilter>>(SCHLUESSEL.laenderFilter, LAENDERFILTER_VORGABE);
  return {
    eu: typeof wert.eu === "boolean" ? wert.eu : LAENDERFILTER_VORGABE.eu,
    china: typeof wert.china === "boolean" ? wert.china : LAENDERFILTER_VORGABE.china,
  };
}

/** `writeLocalJson('hubCountryFilter', ...)` - Schreibpartner von `leseLaenderFilter`. */
export function schreibeLaenderFilter(filter: LaenderFilter): void {
  writeLocalJson(SCHLUESSEL.laenderFilter, filter);
}

/** Vorgabe, solange nichts gespeichert ist: alle drei Transportarten sichtbar. */
const TRANSPORTART_FILTER_VORGABE: TransportartFilter = { see: true, air: true, road: true };

/**
 * `hubTransportModeFilter` - NEUBAU ohne Bestandsvorbild, siehe Kopfkommentar „Der siebte
 * Schluessel". Wortgleiches Muster zu `leseLaenderFilter`: JSON-Form, Vorgabe beim Fehlen/bei
 * kaputtem JSON `{ see: true, air: true, road: true }` (alle drei Transportarten sichtbar).
 *
 * `road` NACHGEZOGEN (Asana 1218176179911324): gespeicherte Filter aus der Zeit vor dieser
 * Aenderung haben kein `road` im JSON - die `typeof … === "boolean"`-Pruefung faengt das wie jedes
 * andere fehlende/kaputte Feld ab und liefert die Vorgabe `true`. Deshalb kein Umzug und keine
 * Versionsanhebung des Schluessels `hubTransportModeFilter` noetig.
 */
export function leseTransportartFilter(): TransportartFilter {
  const wert = readLocalJson<Partial<TransportartFilter>>(
    SCHLUESSEL.transportartFilter,
    TRANSPORTART_FILTER_VORGABE,
  );
  return {
    see: typeof wert.see === "boolean" ? wert.see : TRANSPORTART_FILTER_VORGABE.see,
    air: typeof wert.air === "boolean" ? wert.air : TRANSPORTART_FILTER_VORGABE.air,
    road: typeof wert.road === "boolean" ? wert.road : TRANSPORTART_FILTER_VORGABE.road,
  };
}

/** `writeLocalJson('hubTransportModeFilter', ...)` - Schreibpartner von `leseTransportartFilter`. */
export function schreibeTransportartFilter(filter: TransportartFilter): void {
  writeLocalJson(SCHLUESSEL.transportartFilter, filter);
}

/** `DRAWER_WIDTH` (index.html:4640). Siehe Eigenheit 4: die gespeicherte 0 ergibt 640. */
export function leseDrawerBreite(): number {
  return (
    Number(readLocalValue(SCHLUESSEL.drawerBreite, String(DRAWER_BREITE_VORGABE))) ||
    DRAWER_BREITE_VORGABE
  );
}

/** index.html:9237 - `String(Math.round(DRAWER_WIDTH))`, kein JSON. */
export function schreibeDrawerBreite(breite: number): void {
  writeLocalValue(SCHLUESSEL.drawerBreite, String(Math.round(breite)));
}
